import { RoleDTO } from '@/core/api/sicom-api/sistema/roles/roles-admin.model';

export type CrudTipo = 'C' | 'R' | 'U' | 'D';

export interface AcaoRole {
    role: string;
    acao: string;
    acaoLabel: string;
    descricao?: string;
    crud: CrudTipo | null;
}
export interface FuncionalidadeGrupo {
    funcionalidade: string;
    funcionalidadeLabel: string;
    crud: AcaoRole[];
    especificas: AcaoRole[];
    todas: AcaoRole[];
}
export interface ModuloGrupo {
    modulo: string;
    moduloLabel: string;
    total: number;
    funcionalidades: FuncionalidadeGrupo[];
}

const MAP_CRUD: Record<string, CrudTipo> = {
    visualizar: 'R',
    listar: 'R',
    ver: 'R',
    consultar: 'R',
    criar: 'C',
    incluir: 'C',
    adicionar: 'C',
    cadastrar: 'C',
    editar: 'U',
    alterar: 'U',
    atualizar: 'U',
    excluir: 'D',
    remover: 'D',
    deletar: 'D'
};
const ORDEM_CRUD: CrudTipo[] = ['C', 'R', 'U', 'D'];

export const PADRAO_ROLE = /^[a-z0-9-]+(\.[a-z0-9-]+)+$/;

/** Classifica o último segmento da role no tipo CRUD (C/R/U/D), ou null se não for uma ação CRUD conhecida. */
export function classificarCrud(ultimoSegmento: string): CrudTipo | null {
    return MAP_CRUD[ultimoSegmento] ?? null;
}
/** Valida o formato da role: segmentos minúsculos/numéricos separados por ponto (modulo.funcionalidade[.acao]). */
export function roleValida(name: string): boolean {
    return PADRAO_ROLE.test(name);
}
/** Monta a role juntando módulo, funcionalidade e ação com ponto (em minúsculo, ignorando segmentos vazios). */
export function montarRole(modulo: string, funcionalidade: string, acao: string): string {
    return [modulo, funcionalidade, acao]
        .map((s) => (s ?? '').trim().toLowerCase())
        .filter(Boolean)
        .join('.');
}
/** Rótulos acentuados por segmento (módulo/funcionalidade/ação). Só para exibir — a role segue minúscula/sem acento. */
const ROTULOS: Record<string, string> = {
    // módulos
    arrecadacao: 'Arrecadação',
    comissoes: 'Comissões',
    configuracoes: 'Configurações',
    'divida-ativa': 'Dívida Ativa',
    medicos: 'Médicos',
    orgaos: 'Órgãos',
    'pessoa-fisica': 'Pessoa Física',
    'pessoa-juridica': 'Pessoa Jurídica',
    // funcionalidades
    abrangencia: 'Abrangência',
    'alteracao-cadastral': 'Alteração Cadastral',
    anotacoes: 'Anotações',
    'area-atuacao': 'Área de Atuação',
    'averbacao-nacionalidade': 'Averbação de Nacionalidade',
    'averbacao-nome': 'Averbação de Nome',
    'cancelamento-falecimento': 'Cancelamento por Falecimento',
    'cancelamento-inscricao': 'Cancelamento de Inscrição',
    'cancelamento-pj': 'Cancelamento PJ',
    cartao: 'Cartão',
    'cedula-identidade': 'Cédula de Identidade',
    'central-transferencia': 'Central de Transferência',
    cobranca: 'Cobrança',
    'comissao-etica': 'Comissão de Ética',
    composicao: 'Composição',
    'conflito-interesse': 'Conflito de Interesse',
    'consulta-nsu': 'Consulta NSU',
    'consulta-receita': 'Consulta Receita',
    consultorio: 'Consultório',
    'consultorio-privado': 'Consultório Privado',
    convocacoes: 'Convocações',
    'corpo-societario': 'Corpo Societário',
    creditos: 'Créditos',
    'diretor-clinico': 'Diretor Clínico',
    email: 'E-mail',
    'emails-inadimplentes': 'E-mails Inadimplentes',
    'emissao-valid': 'Emissão VALID',
    'especialidade-medica': 'Especialidade Médica',
    estatisticas: 'Estatísticas',
    formacao: 'Formação',
    funcoes: 'Funções',
    historico: 'Histórico',
    isencoes: 'Isenções',
    medico: 'Médico',
    modulos: 'Módulos',
    'motivo-afastamento': 'Motivo de Afastamento',
    pcmso: 'PCMSO',
    permissoes: 'Permissões',
    'pesquisar-boletos': 'Pesquisar Boletos',
    'pos-graduacao': 'Pós-Graduação',
    'pre-medico': 'Pré-Médico',
    'pre-prestador': 'Pré-Prestador',
    psmt: 'PSMT',
    refis: 'REFIS',
    relatorios: 'Relatórios',
    'renovacao-cadastral': 'Renovação Cadastral',
    reunioes: 'Reuniões',
    servicos: 'Serviços',
    'servicos-especialidades': 'Serviços e Especialidades',
    'solicitacao-diretor': 'Solicitação de Diretor',
    'solicitacao-diretor-tecnico': 'Solicitação de Diretor Técnico',
    usuarios: 'Usuários',
    'visto-temporario': 'Visto Temporário',
    // ações
    // Roles de 4 segmentos: `agruparRoles` junta tudo após o 2º segmento numa "ação" só, então a
    // chave do rótulo é a ação composta.
    'permissoes.administrar': 'Permissões — Administrar',
    'permissoes.catalogo': 'Permissões — Catálogo',
    'permissoes.diagnostico': 'Permissões — Diagnóstico',
    convenio: 'Convênio',
    diagnostico: 'Diagnóstico',
    'desfazer-negociacao': 'Desfazer Negociação',
    'enviar-email': 'Enviar E-mail',
    'gerar-debito': 'Gerar Débito',
    'gerar-debitos-massa': 'Gerar Débitos em Massa',
    'inscricao-secundaria': 'Inscrição Secundária',
    negociar: 'Negociar',
    isencao: 'Isenção',
    'remover-debito': 'Remover Débito'
};

/** Nome amigável p/ exibição: usa rótulo acentuado se houver, senão troca '-' por espaço e capitaliza. A role segue minúscula. */
export function rotular(segmento: string): string {
    const chave = segmento.toLowerCase();
    if (ROTULOS[chave]) return ROTULOS[chave];
    return chave
        .split('-')
        .map((p) => (p ? p.charAt(0).toUpperCase() + p.slice(1) : p))
        .join(' ');
}

/** Agrupa as roles por módulo → funcionalidade → ações (CRUD ordenado + específicas) para o catálogo/kanban. */
export function agruparRoles(roles: RoleDTO[]): ModuloGrupo[] {
    const modulos = new Map<string, Map<string, AcaoRole[]>>();
    for (const r of roles) {
        const segs = (r.name ?? '').split('.');
        if (segs.length < 2) continue;
        const modulo = segs[0];
        const funcionalidade = segs[1];
        const acao = segs.slice(2).join('.') || '—';
        const crud = segs.length >= 3 ? classificarCrud(segs[segs.length - 1]) : null;
        const ac: AcaoRole = { role: r.name, acao, acaoLabel: acao === '—' ? '—' : rotular(acao), descricao: r.description, crud };
        if (!modulos.has(modulo)) modulos.set(modulo, new Map());
        const funcs = modulos.get(modulo)!;
        if (!funcs.has(funcionalidade)) funcs.set(funcionalidade, []);
        funcs.get(funcionalidade)!.push(ac);
    }
    const ordenarCrud = (a: AcaoRole, b: AcaoRole) => ORDEM_CRUD.indexOf(a.crud!) - ORDEM_CRUD.indexOf(b.crud!);
    const resultado: ModuloGrupo[] = [];
    for (const [modulo, funcs] of [...modulos.entries()].sort((a, b) => a[0].localeCompare(b[0]))) {
        const funcionalidades: FuncionalidadeGrupo[] = [];
        let total = 0;
        for (const [funcionalidade, acoes] of [...funcs.entries()].sort((a, b) => a[0].localeCompare(b[0]))) {
            const crud = acoes.filter((a) => a.crud).sort(ordenarCrud);
            const especificas = acoes.filter((a) => !a.crud).sort((a, b) => a.acao.localeCompare(b.acao));
            funcionalidades.push({ funcionalidade, funcionalidadeLabel: rotular(funcionalidade), crud, especificas, todas: [...crud, ...especificas] });
            total += acoes.length;
        }
        resultado.push({ modulo, moduloLabel: rotular(modulo), total, funcionalidades });
    }
    return resultado;
}
